import { APP_INITIALIZER, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';

import { DomainResolveApiService } from '../api/services/domain-resolve-api.service';
import { BrandingService } from '../services/branding.service';

/** Hosts that are always the main (unbranded) app — never resolved. */
const MAIN_HOSTS = [
  'zaklepuj.pl',
  'www.zaklepuj.pl',
  'panel.zaklepuj.pl',
  'admin.zaklepuj.pl',
  // Staging / dev main app hosts — explicit so the marketplace renders unbranded
  // (they also match RESERVED_LABELS, but pinning them here is robust to changes).
  'staging.zaklepuj.pl',
  'dev.zaklepuj.pl',
  'localhost',
  '127.0.0.1',
];

/** Reserved first labels that are infrastructure, not customer subdomains. */
const RESERVED_LABELS = [
  'www',
  'panel',
  'admin',
  'api',
  'mx',
  'mail',
  'files',
  'dev',
  'staging',
  'cdn-files',
  'n8n',
  'notify',
  'send',
];

/** IPv4 literal (e.g. 192.168.1.5) — never a white-label host. */
const IP_LITERAL = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * APP_INITIALIZER that detects a branded host and, if so, resolves its
 * white-label config before the app renders. A 404 / error / timeout never
 * rejects — the app simply renders unbranded.
 *
 * Dev/testing: append `?previewHost=<host>` to force resolution of any host
 * (e.g. http://localhost:4200/?previewHost=kolorovewakacje.zaklepuj.pl).
 */
export function provideWhiteLabel() {
  return [
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: () => {
        const resolveApi = inject(DomainResolveApiService);
        const branding = inject(BrandingService);
        const transloco = inject(TranslocoService);
        return () => {
          const host = detectBrandedHost();
          if (!host) {
            return Promise.resolve();
          }
          return firstValueFrom(
            resolveApi.resolve(host).pipe(
              timeout(5000),
              catchError(() => of(null)),
            ),
          )
            .then((res) => {
              if (res) {
                branding.apply(res);
                applyDefaultLang(transloco, res.branding?.defaultLang);
              }
            })
            .catch(() => undefined);
        };
      },
    },
  ];
}

/**
 * Switch the active Transloco language (and <html lang>) to the host's default,
 * when one is configured. Never throws — language stays unchanged on failure.
 */
function applyDefaultLang(transloco: TranslocoService, lang?: string | null): void {
  if (!lang) {
    return;
  }
  try {
    transloco.setActiveLang(lang);
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  } catch {
    // White-label must never break the app over a language preference.
  }
}

/**
 * Returns the host to resolve, or null when the main app should render.
 *
 * Resolves BOTH our customer subdomains (`*.zaklepuj.pl`) AND customers' own
 * custom domains (e.g. `offers.yourcompany.com`). Only our own infra hosts and
 * IP literals are excluded; for unknown hosts the backend simply returns 404
 * and the caller renders the main app unbranded.
 *
 * Exported (with an injectable `loc`) so it can be unit-tested without stubbing
 * `window.location`.
 */
export function detectBrandedHost(
  loc: { hostname: string; search: string } | null = typeof window !== 'undefined'
    ? { hostname: window.location.hostname, search: window.location.search }
    : null,
): string | null {
  if (!loc) {
    return null;
  }

  const preview = new URLSearchParams(loc.search).get('previewHost');
  if (preview) {
    return preview.trim().toLowerCase();
  }

  const hostname = loc.hostname.toLowerCase();

  // Our own infrastructure hosts are always the main, unbranded app.
  if (MAIN_HOSTS.includes(hostname)) {
    return null;
  }

  // Never resolve bare IP literals (IPv4 or IPv6) — no white-label host is an IP.
  if (IP_LITERAL.test(hostname) || hostname.includes(':')) {
    return null;
  }

  // Within our own zone, skip reserved infrastructure subdomains (www/api/mx/…).
  // For customers' own domains this check is irrelevant and must not apply.
  if (
    hostname.endsWith('.zaklepuj.pl') &&
    RESERVED_LABELS.includes(hostname.split('.')[0])
  ) {
    return null;
  }

  // Any other host — a customer subdomain or their own custom domain — is
  // resolved; the backend decides (200 branded / 404 unbranded).
  return hostname;
}
